import React, { useContext } from "react";
import { NetworkStatus } from "@apollo/client";
import { ColumnDef } from "@tanstack/table-core/build/lib/types";
import { useLocation } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import { buildLink } from "common/routing";
import { formatAddressFromAddressParts } from "common/utils/address";
import { QueryDescription, TABLE_NAMES } from "common/utils/queryBuilder";
import { buildGuestPropertyURL } from "common/utils/url";
import {
  GetQueryPropertiesQueryVariables,
  useGetPropertyColumnConfigQuery,
  useGetQueryPropertiesLazyQuery,
  useGetSavedViewsQuery,
} from "../../generated/graphql";
import { useLocalTableDisplayConfig } from "../../hooks/useTableDisplayConfig";
import { useDeletePropertyModal } from "../AddressPanel/Header/DeletePropertyModal";
import { AuthContext } from "../Authorization/AuthContext";
import { ACTION_COLUMN_DEF_CONSTANTS, ActionCell } from "../Common/ActionCell";
import {
  filesCell,
  pillsCell,
  TasksCell,
} from "../Common/FullWidthTable/customCells";
import { FullWidthTable } from "../Common/FullWidthTable/FullWidthTable";
import { Attribute } from "../Common/FullWidthTable/types";
import {
  fieldId,
  initializeColumns,
  mergeFieldGroups,
  MinimalColumnDef,
  sanityCheckLocalColumnOrder,
} from "../Common/FullWidthTable/utils";
import LinkCell from "../Common/LinkCell";
import {
  useManualPaginationConfig,
  useQueryDescription,
} from "../Common/Tables/hooks";
import {
  buildColumnDefinitions,
  CellRenderConfig,
} from "../Common/Tables/utils";
import {
  INITIAL_SUBMISSION_TYPE_REPORTS,
  SDE_DATABASE_REPORT,
} from "../Exports/constants";
import ExportDataButton from "../Exports/ExportDataButton";
import { buildInitialReportTypes } from "../Exports/ExportDataForm";
import { ActionsProps } from "../Inputs/DropdownMenu";
import { QueryPropertyTableResult } from "./__queries__/table";
export const PROPERTIES_TABLE_REQUIRED_FIELDS = [
  { table: TABLE_NAMES.PROPERTIES, name: "id" },
  { table: TABLE_NAMES.PROPERTIES, name: "latitude" },
  { table: TABLE_NAMES.PROPERTIES, name: "longitude" },
  { table: TABLE_NAMES.PROPERTIES, name: "zipcode" },
  { table: TABLE_NAMES.PROPERTIES, name: "city" },
  { table: TABLE_NAMES.PROPERTIES, name: "state" },
  { table: TABLE_NAMES.PROPERTIES, name: "streetAddress" },
] as const;
import { someActionsEnabled } from "../Common/ActionBar/utils";

const propertyLink = ({ admin }: { admin: boolean }) => {
  return {
    cell: ({ row }: { row: { original: QueryPropertyTableResult } }) => {
      const { account } = useContext(AuthContext);
      const props =
        admin ?
          row.original["Properties.adminLink"]
        : row.original["Properties.userLink"];

      if (!props) {
        return null;
      }

      const { latitude, longitude, propertyId, streetAddress } = props;

      const url = buildLink(
        "map",
        { accountId: account?.id! },
        {
          propertyId,
          lat: latitude,
          lng: longitude,
          address: streetAddress,
        },
      );

      return <LinkCell href={url} label={streetAddress} target={"_blank"} />;
    },
  };
};

export const CUSTOM_PROPERTY_LINK_CELLS = {
  "Properties.adminLink": propertyLink({ admin: true }),
  "Properties.publicLink": {
    cell: ({ row }: { row: { original: QueryPropertyTableResult } }) => {
      const linkProps = row.original["Properties.publicLink"];
      const address = row.original["Properties.streetAddress"];

      if (!linkProps) {
        return null;
      }

      const url = buildGuestPropertyURL({
        subdomain: linkProps.subdomain,
        propertyId: linkProps.propertyId,
      });

      return <LinkCell href={url} target={"_blank"} label={address} />;
    },
  },
  "Properties.streetAddress": {
    cell: ({ row }: { row: { original: QueryPropertyTableResult } }) => {
      const { isPublic } = useContext(AuthContext);
      const address = row.original["Properties.streetAddress"];

      if (!row.original?.["Properties.id"]) {
        return address;
      }

      const url =
        isPublic ?
          buildLink("residentProperty", {
            propertyId: row.original["Properties.id"],
          })
        : buildLink(
            "map",
            {},
            {
              lat: row.original["Properties.latitude"],
              lng: row.original["Properties.longitude"],
              propertyId: row.original["Properties.id"],
            },
          );
      if (!url) {
        return address;
      }
      return <LinkCell href={url} target="_blank" label={address} />;
    },
  },
  "Properties.userLink": propertyLink({ admin: false }),
  ["Properties.tasks"]: {
    cell: ({ row }: { row: { original: QueryPropertyTableResult } }) => {
      const tasks = row.original[`${TABLE_NAMES.PROPERTIES}.tasks`] || [];
      return <TasksCell tasks={tasks} />;
    },
  },
};

export const PROPERTY_TABLE_CUSTOM_CELLS: Record<string, CellRenderConfig> = {
  "Properties.warnings": {
    cell: ({ row }) => {
      return pillsCell({
        row: row.original["Properties.warnings"]?.map(
          (w: { title: string }) => w.title,
        ),
        iconName: "alert-circle",
      });
    },
  },
  "Properties.files": {
    cell: ({ row }) => {
      return filesCell({ row: row.original["Properties.files"] });
    },
  },
  ...CUSTOM_PROPERTY_LINK_CELLS,
};

const PropertyActions = ({
  propertyData,
  onDelete,
}: {
  propertyData: {
    "Properties.id": string;
    "Properties.streetAddress": Maybe<string>;
    "Properties.city": Maybe<string>;
    "Properties.latitude": number;
    "Properties.longitude": number;
    "Properties.state": Maybe<string>;
    "Properties.zipcode": Maybe<string>;
  };
  onDelete: () => void;
}) => {
  const id = propertyData["Properties.id"];
  const streetAddress = propertyData["Properties.streetAddress"];
  const city = propertyData["Properties.city"];
  const latitude = propertyData["Properties.latitude"];
  const longitude = propertyData["Properties.longitude"];

  const { authorized, account } = useContext(AuthContext);
  const [showDeletePropertyModal, hideDeletePropertyModal] =
    useDeletePropertyModal({
      property: {
        id,
        streetAddress,
        city,
        fullAddress: formatAddressFromAddressParts({
          streetAddress,
          city,
          state: propertyData["Properties.state"],
          zipcode: propertyData["Properties.zipcode"],
        }),
        latitude,
        longitude,
      },
      updateMap: () => {},
      closeModal: () => {
        hideDeletePropertyModal();
      },
      onDelete: () => {
        hideDeletePropertyModal();
        onDelete();
      },
    });

  const actions: Array<ActionsProps> = [
    {
      label: "View",
      href: buildLink(
        "map",
        { accountId: account?.id! },
        {
          lat: latitude.toString(),
          lng: longitude.toString(),
          address: streetAddress ?? "",
          propertyId: id,
        },
      ),
      target: "_blank",
    },
    {
      label: "Delete",
      onClick: () => {
        showDeletePropertyModal();
      },
      variant: "red",
      disabled: !authorized({
        resource: RESOURCE_NAME.PROPERTY,
        permission: "delete",
      }),
    },
  ];

  return <ActionCell actions={actions} />;
};

export const PropertiesTable = () => {
  const { user, admin, account } = useContext(AuthContext);

  const {
    data: savedViewsResponse,
    loading: loadingSavedViews,
    refetch: refetchSavedViews,
  } = useGetSavedViewsQuery({
    variables: { table: TABLE_NAMES.PROPERTIES },
    fetchPolicy: "cache-and-network",
  });

  const { data: columnConfigResponse, loading: loadingColumnConfig } =
    useGetPropertyColumnConfigQuery({ fetchPolicy: "network-only" });

  const [
    queryProperties,
    { previousData, data: currentData, networkStatus, loading, error, refetch },
  ] = useGetQueryPropertiesLazyQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const actionsColumn: MinimalColumnDef<
    typeof PROPERTIES_TABLE_REQUIRED_FIELDS
  > = {
    ...ACTION_COLUMN_DEF_CONSTANTS,
    cell: ({ row }) => {
      const property = row.original;
      return <PropertyActions propertyData={property} onDelete={refetch} />;
    },
  };

  const {
    initialTableState,
    queryDescription,
    updateQueryDescription,
    currentView,
  } = useQueryDescription({
    source: { table: TABLE_NAMES.PROPERTIES },
    defaultSort: [{ id: "Properties.streetAddress", desc: false }],
    savedViews: savedViewsResponse?.account?.savedViews,
  });

  const generatedColumnDefinitions = buildColumnDefinitions({
    columnConfig: columnConfigResponse?.getPropertyTableConfig.data || [],
    cellRenderers: PROPERTY_TABLE_CUSTOM_CELLS,
  });

  const { pathname } = useLocation();

  const tableId = `${user?.id ?? admin?.id}-${pathname.replace("/", "")}`;
  const { getLocalTableState, setLocalColumnOrder } =
    useLocalTableDisplayConfig({
      tableId,
      defaultValue: {
        columnOrder: [
          ...queryDescription.fields.map(field => fieldId(field)),
          ACTION_COLUMN_DEF_CONSTANTS.id,
        ],
        columnSizing: {},
        sorting: [],
      },
    });

  const manualPaginationConfig = useManualPaginationConfig({
    ...initialTableState.pagination,
    currentTotalPages: currentData?.queryProperties.pageInfo.totalPages,
    previousTotalPages: previousData?.queryProperties.pageInfo.totalPages,
  });

  const tanstackColumnDefinitions: Array<ColumnDef<QueryPropertyTableResult>> =
    [...generatedColumnDefinitions, actionsColumn];

  const data =
    networkStatus === NetworkStatus.setVariables ? previousData : currentData;

  if (
    loadingColumnConfig ||
    !columnConfigResponse?.getPropertyTableConfig.data ||
    loadingSavedViews ||
    !currentView
  ) {
    return null;
  }

  const loadingDetails = {
    loading,
    loadingText: "Loading properties",
    noDataText: "No properties found",
  };

  const search = ({ page }: { page: number }) => {
    const variables: GetQueryPropertiesQueryVariables = {
      description: {
        ...queryDescription,
        fields: mergeFieldGroups(
          queryDescription.fields,
          PROPERTIES_TABLE_REQUIRED_FIELDS,
        ),
      } satisfies QueryDescription,
      page,
    };

    void queryProperties({
      variables,
    });
  };

  const localTableConfig = getLocalTableState();

  const { columnOrder: localColumnOrder } = localTableConfig;

  const updatedColumnOrder = sanityCheckLocalColumnOrder({
    localColumnOrder,
    defaultColumnIds: queryDescription.fields.map(field => fieldId(field)),
  });

  if (updatedColumnOrder) {
    setLocalColumnOrder(updatedColumnOrder);
  }

  const actionBarConfig =
    columnConfigResponse?.getPropertyTableConfig.actionBarConfig;
  const enableSelectColumn = someActionsEnabled(actionBarConfig?.actions ?? {});

  const initialColumns = initializeColumns({
    initialTableState,
    tanstackColumnDefinitions,
    initialQueryDescription: queryDescription,
    actionsColumn,
    enableSelectColumn,
  });

  const timeoutError = error?.graphQLErrors.find(
    e => e.extensions.code === "TIMEOUT_ERROR",
  );

  const initialReportTypes = buildInitialReportTypes({
    initialReportTypes: [SDE_DATABASE_REPORT],
    account,
    submissionTypeNames: [INITIAL_SUBMISSION_TYPE_REPORTS.SI_SD_REPORT],
  });

  const actions = <ExportDataButton initialReportTypes={initialReportTypes} />;

  return (
    <FullWidthTable
      columns={initialColumns}
      previousData={previousData?.queryProperties.data}
      currentData={data?.queryProperties.data ?? []}
      loadingDetails={loadingDetails}
      manualPaginationConfig={{
        ...manualPaginationConfig,
        pageCount: data?.queryProperties.pageInfo.totalPages ?? 1,
      }}
      initialState={initialTableState}
      filterable={{
        filterConfiguration: columnConfigResponse.getPropertyTableConfig
          .data as Array<Attribute>,
        search,
      }}
      columnSettingProps={{
        columnConfiguration: columnConfigResponse.getPropertyTableConfig.data,
        columnDefinitions: tanstackColumnDefinitions,
      }}
      timeoutError={timeoutError}
      queryDescription={queryDescription}
      currentView={currentView}
      updateQueryDescription={updateQueryDescription}
      savedViews={savedViewsResponse?.account?.savedViews}
      refetchSavedViews={refetchSavedViews}
      refetchTable={refetch}
      actions={actions}
      clientSideColumnConfig={columnConfigResponse.getPropertyTableConfig.data}
      dataSourceComputationsAffectingQuery={
        data?.queryProperties.dataSourceComputationsAffectingQuery
      }
      rowIdField={`${TABLE_NAMES.PROPERTIES}.id`}
      totalCount={data?.queryProperties.pageInfo.totalCount ?? 0}
      actionBarConfig={actionBarConfig}
    />
  );
};
